import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { VideoOff, Film, Images, SwitchCamera } from "lucide-react";
import { openVideoStream, createRecorder, bitrateFor, isIOS } from "@/lib/recorder";

const MAX = 60;
const mb = (bytes) => +(bytes / 1048576).toFixed(2);

const PHOTO_CONSTRAINTS = { width: { ideal: 4096 }, height: { ideal: 3072 } };

const openPhotoStream = (facing) => navigator.mediaDevices.getUserMedia({ video: { facingMode: facing, ...PHOTO_CONSTRAINTS }, audio: false })
  .catch(() => navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false }));

function useCamera(facing, mode, videoRef, maxRes) {
  const streamRef = useRef(null);
  const [ok, setOk] = useState(null);
  const [res, setRes] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const el = videoRef.current;
    if (!navigator.mediaDevices?.getUserMedia) { setOk(false); return; }
    setOk(null);
    (mode === "photo" ? openPhotoStream(facing) : openVideoStream(facing, maxRes))
      .then((s) => {
        if (cancelled || !videoRef.current) return s.getTracks().forEach((t) => t.stop());
        streamRef.current = s;
        videoRef.current.srcObject = s;
        videoRef.current.play?.().catch(() => {});
        const st = s.getVideoTracks()[0]?.getSettings() || {};
        setRes(st.width && st.height ? { w: st.width, h: st.height } : null);
        setOk(true);
      })
      .catch(() => setOk(false));
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (el) el.srcObject = null;
    };
  }, [facing, mode, videoRef, maxRes]);
  return { ok, streamRef, res };
}

const resLabel = (r) => (r ? `${Math.min(r.w, r.h) >= 1080 ? "1080p" : Math.min(r.w, r.h) >= 720 ? "720p" : `${Math.min(r.w, r.h)}p`} · ${r.w}×${r.h}` : "");

function ModeToggle({ mode, setMode, disabled }) {
  const opt = (k, l) => (
    <button key={k} disabled={disabled} onClick={() => setMode(k)} data-testid={`capture-mode-${k}`}
      className={`rounded-full px-4 py-1.5 text-xs font-extrabold transition-colors ${mode === k ? "bg-white text-black" : "text-white/70"}`}>{l}</button>
  );
  return <div className="flex rounded-full bg-black/60 p-1 backdrop-blur">{opt("photo", "Foto")}{opt("video", "Vídeo")}</div>;
}

export function CaptureStep({ onDone }) {
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const secsRef = useRef(0);
  const [mode, setMode] = useState("video");
  const [facing, setFacing] = useState("user");
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const [maxRes, setMaxRes] = useState(1080);
  const { ok, streamRef, res } = useCamera(facing, mode, videoRef, maxRes);

  const downgrade = (msg) => {
    if (maxRes > 720) { setMaxRes(720); toast.warning(msg, { description: "Ajustamos a câmera para 720p (HD). Grave novamente." }); }
    else toast.error(msg, { description: "Tente gravar novamente ou carregue da galeria." });
  };

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => { secsRef.current += 1; setSecs(secsRef.current); if (secsRef.current >= MAX) recRef.current?.stop(); }, 1000);
    return () => clearInterval(t);
  }, [recording]);

  const canvasPhoto = () => {
    const v = videoRef.current;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    const ctx = c.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    if (facing === "user") { ctx.translate(c.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((b) => onDone({ type: "photo", previewUrl: URL.createObjectURL(b), file: new File([b], "foto.jpg", { type: "image/jpeg" }), duration: 0, sizeMb: mb(b.size), source: "camera" }), "image/jpeg", 0.95);
  };

  const takePhoto = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (track && "ImageCapture" in window) {
      try {
        const ic = new window.ImageCapture(track);
        const caps = await ic.getPhotoCapabilities();
        const b = await ic.takePhoto({ imageWidth: caps.imageWidth?.max, imageHeight: caps.imageHeight?.max });
        return onDone({ type: "photo", previewUrl: URL.createObjectURL(b), file: new File([b], "foto.jpg", { type: b.type || "image/jpeg" }), duration: 0, sizeMb: mb(b.size), source: "camera" });
      } catch {}
    }
    canvasPhoto();
  };

  const startRec = () => {
    chunks.current = [];
    secsRef.current = 0;
    if (!window.MediaRecorder || !streamRef.current) return toast.error("Gravação não suportada neste navegador");
    let rec, mimeType;
    try { ({ rec, mimeType } = createRecorder(streamRef.current, bitrateFor(res))); } catch { return downgrade("Não foi possível iniciar a gravação"); }
    rec.ondataavailable = (e) => { if (e.data?.size) chunks.current.push(e.data); };
    rec.onerror = () => { setRecording(false); downgrade("A gravação falhou"); };
    rec.onstop = () => {
      setRecording(false);
      const raw = rec.mimeType || mimeType || chunks.current[0]?.type || (isIOS ? "video/mp4" : "video/webm");
      const type = raw.split(";")[0] || "video/mp4";
      const blob = new Blob(chunks.current, { type });
      if (!blob.size) return downgrade("O vídeo gravado ficou vazio");
      onDone({ type: "video", previewUrl: URL.createObjectURL(blob), file: new File([blob], `video.${type.includes("mp4") ? "mp4" : "webm"}`, { type }), duration: Math.max(1, secsRef.current), sizeMb: mb(blob.size), source: "camera" });
    };
    try { isIOS ? rec.start() : rec.start(1000); } catch { return downgrade("Não foi possível iniciar a gravação"); }
    recRef.current = rec;
    setSecs(0);
    setRecording(true);
  };

  const shutter = () => (mode === "photo" ? takePhoto() : recording ? recRef.current?.stop() : startRec());

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (file.type.startsWith("image/")) return onDone({ type: "photo", previewUrl: url, file, duration: 0, sizeMb: mb(file.size), source: "gallery" });
    if (!file.type.startsWith("video/")) return toast.error("Escolha uma foto ou um vídeo");
    onDone({ type: "video", previewUrl: url, file, duration: 0, sizeMb: mb(file.size), source: "gallery" });
  };

  const demo = () => onDone(mode === "photo"
    ? { type: "photo", previewUrl: "/shorts/poster2.jpg", duration: 0, sizeMb: 0, source: "sample" }
    : { type: "video", previewUrl: "/shorts/clip1.mp4", duration: 15, sizeMb: 0, source: "sample" });

  return (
    <div className="relative h-full w-full overflow-hidden rounded-3xl bg-card" data-testid="camera-step">
      <video ref={videoRef} autoPlay muted playsInline className={`absolute inset-0 h-full w-full object-cover ${facing === "user" ? "-scale-x-100" : ""} ${ok ? "" : "hidden"}`} data-testid="camera-preview" />
      {ok === false && (
        <div className="absolute inset-0 grid place-items-center p-8 text-center text-sm text-muted-foreground" data-testid="camera-unavailable">
          <div><VideoOff size={40} className="mx-auto mb-3" />Câmera indisponível. Carregue da galeria ou use um exemplo.</div>
        </div>
      )}
      {mode === "video" && (
        <div className="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 font-mono-k text-sm" data-testid="record-timer">
          {recording && <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-red-500 live-dot" />}
          00:{String(secs).padStart(2, "0")} / 01:00
        </div>
      )}
      <button onClick={() => setFacing(facing === "user" ? "environment" : "user")} disabled={recording} data-testid="camera-flip-button" aria-label="Inverter câmera"
        className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full bg-black/60 backdrop-blur transition-transform active:rotate-180 disabled:opacity-40">
        <SwitchCamera size={19} />
      </button>
      <span className="absolute right-3 top-16 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold" data-testid="camera-facing-label">{facing === "user" ? "Selfie" : "Traseira"}</span>
      {ok && res && <span className="absolute left-3 top-3 rounded-full bg-black/60 px-2 py-0.5 font-mono-k text-[10px] font-bold text-emerald-300" data-testid="camera-resolution-label">{resLabel(res)}</span>}
      <div className="absolute inset-x-0 bottom-5 flex flex-col items-center gap-4">
        <ModeToggle mode={mode} setMode={setMode} disabled={recording} />
        <div className="flex w-full items-center justify-around px-6">
          <button onClick={() => fileRef.current.click()} disabled={recording} className="flex flex-col items-center gap-1 text-[11px] font-bold" data-testid="gallery-upload-button">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-black/60 ring-1 ring-white/30"><Images size={19} /></span> Galeria
          </button>
          <button onClick={shutter} disabled={!ok} className="grid h-20 w-20 place-items-center rounded-full border-4 border-white disabled:opacity-40" data-testid="record-toggle-button" aria-label={mode === "photo" ? "Tirar foto" : recording ? "Parar" : "Gravar"}>
            <span className={`transition-all ${mode === "photo" ? "h-16 w-16 rounded-full bg-white" : recording ? "h-8 w-8 rounded-lg bg-red-600" : "h-16 w-16 rounded-full bg-red-600"}`} />
          </button>
          <button onClick={demo} disabled={recording} className="flex flex-col items-center gap-1 text-[11px] font-bold" data-testid="use-sample-clip-button">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-black/60"><Film size={18} /></span> Exemplo
          </button>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*,video/*" className="hidden" onChange={pickFile} data-testid="gallery-file-input" />
    </div>
  );
}
